import React, { useState, useEffect, useRef } from 'react';
import { Box, Input, Text, Flex } from '@chakra-ui/react';
import { Menu } from '@chakra-ui/react';
import { MdExpandMore, MdSearch } from 'react-icons/md';
import { useExercisesQuery } from '../query/useExercisesQuery';

interface ExerciseTypeaheadProps {
  value: string;
  onChange: (value: string, exerciseId?: string) => void;
  placeholder?: string;
}

interface Exercise {
  id: string;
  name: string;
}

const ExerciseTypeahead: React.FC<ExerciseTypeaheadProps> = ({ 
  value, 
  onChange, 
  placeholder = "Search exercises..." 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch exercises from the database
  const { data: exercises = [], isLoading } = useExercisesQuery();

  // Filter exercises based on search term
  const filteredExercises = exercises.filter(exercise =>
    exercise.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm(value);
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay closing to allow for clicks on dropdown items
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // Handle exercise selection
  const handleExerciseSelect = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    onChange(exercise.name, exercise.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredExercises.length > 0) {
      handleExerciseSelect(filteredExercises[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box position="relative" w="100%">
      <Box position="relative">
        <Flex align="center" position="relative">
          {/* Search Icon */}
          <Box
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            color="gray.400"
            zIndex={1}
            pointerEvents="none"
          >
            <MdSearch size={20} />
          </Box>
          
          <Input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            pl="40px"
            pr="40px"
          />
          
          {/* Dropdown Arrow */}
          <Box 
            position="absolute" 
            right={3} 
            top="50%" 
            transform="translateY(-50%)" 
            pointerEvents="none"
            color="gray.400"
          >
            <MdExpandMore />
          </Box>
        </Flex>
      </Box>

      {/* Dropdown */}
      {isOpen && (
        <Box
          ref={dropdownRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          {isLoading ? (
            <Box p={3} textAlign="center" color="gray.500">
              Loading exercises...
            </Box>
          ) : filteredExercises.length === 0 ? (
            <Box p={3} textAlign="center" color="gray.500">
              {searchTerm ? 'No exercises found' : 'Start typing to search exercises'}
            </Box>
          ) : (
            filteredExercises.map((exercise) => (
              <Box
                key={exercise.id}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => handleExerciseSelect(exercise)}
              >
                <Text fontSize="sm">{exercise.name}</Text>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default ExerciseTypeahead; 